import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, Auth } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence, Firestore } from 'firebase/firestore';
import { getFunctions, Functions } from 'firebase/functions';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Validate that all required environment variables are present
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  throw new Error(
    '❌ Firebase 配置錯誤：請確認 .env.local 檔案存在且包含所有必要的環境變數。\n' +
    '請參考 .env.example 檔案建立您的 .env.local 檔案。'
  );
}

// Initialize Firebase
const app: FirebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Services
export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
export const functions: Functions = getFunctions(app);

// Enable offline persistence for better UX (reduces Firestore reads by ~50%)
// Data will be cached locally and sync when online
if (typeof window !== 'undefined') {
  enableIndexedDbPersistence(db)
    .then(() => {
      console.log('✅ Firestore 離線快取已啟用 - 重新整理頁面將更快載入');
    })
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        // Multiple tabs open, persistence can only be enabled in one tab at a time
        console.warn('⚠️ 多個分頁開啟，離線快取僅在一個分頁中啟用');
      } else if (err.code === 'unimplemented') {
        // Browser doesn't support persistence
        console.warn('⚠️ 瀏覽器不支援離線快取功能');
      } else {
        console.error('❌ 離線快取啟用失敗:', err);
      }
    });
}

// Configure Google Auth Provider
export const googleProvider = new GoogleAuthProvider();

// IMPORTANT: Force account selection every time to prevent login issues after logout
// This ensures users can always choose which Google account to use
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account chooser
  // hd: 'gmail.com' // Optional: restrict to specific domain
});

export default app;
